import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, User, Search, DollarSign, Lock, CheckCircle, Loader } from 'lucide-react';
import { formatCurrency, validatePin, parseAccountBalance } from '../utils/helpers';
import { useUser } from '../context/UserContext';
import apiService from '../services/api';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';
import axios from 'axios';

const SendMoney = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: contextUser, fetchUserPublic } = useUser();
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Get username from URL if present
  const urlParams = new URLSearchParams(location.search);
  const usernameFromUrl = urlParams.get('username');
  
  // Step 1: Search for username
  const [step, setStep] = useState(usernameFromUrl ? 'confirm' : 'search');
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recipientFound, setRecipientFound] = useState(null);
  
  // Step 2: Confirm and send
  const [recipientUsername, setRecipientUsername] = useState(usernameFromUrl || '');
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');

  useEffect(() => {
    if (!contextUser || !contextUser.telegramName) {
      navigate('/login');
      return;
    }
    setUser(contextUser);
    // Sync live balance from API on every page load
    fetchUserPublic(contextUser.telegramName).then(info => {
      if (info) setUser(prev => prev ? { ...prev, accountBalance: info.accountBalance } : prev);
    });
    
    // If username in URL, verify it exists
    if (usernameFromUrl) {
      selectUser(usernameFromUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextUser?.telegramName, navigate]);

  // Real-time search as user types
  useEffect(() => {
    const searchUsers = async () => {
      if (searchUsername.trim().length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await axios.get(
          `https://tradebot-k4nt.onrender.com/api/payment/SearchUsers-web/${searchUsername.trim()}`
        );
        
        if (response.data && response.data.mainUser) {
          // Filter out current user
          const filteredUsers = response.data.mainUser.filter(
            u => u.telegramName.toLowerCase() !== user?.telegramName.toLowerCase()
          );
          setSearchResults(filteredUsers);
          setShowDropdown(filteredUsers.length > 0);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
        setShowDropdown(false);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchUsername, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectUser = async (username) => {
    const formattedUsername = username.startsWith('@') ? username : `@${username}`;
    
    // Check if sending to self — use contextUser as fallback since user state may not be set yet
    const currentUsername = (user || contextUser)?.telegramName || '';
    if (formattedUsername.toLowerCase() === currentUsername.toLowerCase()) {
      toast.error('Cannot send money to yourself');
      return;
    }

    setRecipientFound({
      username: formattedUsername,
      name: formattedUsername,
      verified: false
    });
    setRecipientUsername(formattedUsername);
    setSearchUsername('');
    setShowDropdown(false);
    setStep('confirm');
    
    // Update URL
    navigate(`/send?username=${username}`);
  };

  const handleSend = async (e) => {
    e.preventDefault();

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > parseAccountBalance(user?.accountBalance)) {
      toast.error('Insufficient balance');
      return;
    }

    if (!validatePin(pin)) {
      toast.error('Please enter a valid 6-digit PIN');
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.sendMoney(
        user.telegramName,
        recipientUsername,
        parseFloat(amount),
        pin
      );

      if (response.status === 200 || response.status === 201) {
        toast.success(`Successfully sent ${formatCurrency(parseFloat(amount))} to ${recipientUsername}`);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Send money error:', error);
      toast.error(error.response?.data?.error || 'Failed to send money. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {step === 'search' ? (
          /* STEP 1: Search for Recipient */
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <div className="mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Search className="text-blue-600" size={28} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 text-center mb-2">
                Send Money
              </h2>
              <p className="text-slate-600 text-center">
                Search for the recipient's username
              </p>
            </div>

            {/* Balance Display */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-slate-600 mb-1">Available Balance</p>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(parseAccountBalance(user?.accountBalance))}
              </p>
            </div>

            {/* Search Username with Dropdown */}
            <div className="mb-6 relative">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Recipient Username
              </label>
              <div className="relative" ref={searchRef}>
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" size={20} />
                <input
                  type="text"
                  placeholder="Type to search (e.g., john123)"
                  value={searchUsername}
                  onChange={(e) => setSearchUsername(e.target.value.replace(/^@/, ''))}
                  onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                  className="w-full pl-11 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  autoFocus
                  autoComplete="off"
                />
                {searchLoading && (
                  <Loader className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-600 animate-spin" size={20} />
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">Start typing to see suggestions</p>

              {/* Search Results Dropdown */}
              {showDropdown && searchResults.length > 0 && (
                <div 
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto"
                >
                  {searchResults.map((result, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => selectUser(result.telegramName)}
                      className="w-full px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-0 flex items-center gap-3"
                    >
                      {/* Avatar */}
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        {result.telegramName.charAt(0).toUpperCase()}
                      </div>
                      
                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900 truncate">
                            {result.telegramName}
                          </p>
                          {result.validated === 'verified' && (
                            <CheckCircle className="text-blue-600 flex-shrink-0" size={16} />
                          )}
                        </div>
                        {result.fullName && (
                          <p className="text-sm text-slate-600 truncate">{result.fullName}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* No Results */}
              {showDropdown && searchResults.length === 0 && !searchLoading && searchUsername.length >= 2 && (
                <div 
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl p-4"
                >
                  <p className="text-sm text-slate-500 text-center">No users found matching "{searchUsername}"</p>
                </div>
              )}
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-sm text-blue-900">
                <strong>Tip:</strong> Start typing a username to see matching users. Click on a user to send them money.
              </p>
            </div>
          </div>
        ) : (
          /* STEP 2: Confirm and Send */
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <div className="mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Send className="text-green-600" size={28} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 text-center mb-2">
                Confirm Transfer
              </h2>
              <p className="text-slate-600 text-center">
                Send money to {recipientUsername}
              </p>
            </div>

            {/* Recipient Info */}
            {recipientFound && (
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 mb-6 border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {recipientFound.username.charAt(1).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-slate-900">{recipientFound.username}</p>
                      {recipientFound.verified && (
                        <CheckCircle className="text-blue-600" size={16} />
                      )}
                    </div>
                    <p className="text-sm text-slate-600">{recipientFound.name}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Balance Display */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-slate-600 mb-1">Available Balance</p>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(parseAccountBalance(user?.accountBalance))}
              </p>
            </div>

            <form onSubmit={handleSend} className="space-y-5">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Amount (USD)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-lg font-semibold"
                    required
                    autoFocus
                  />
                </div>
              </div>

              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-4 gap-2">
                {[10, 25, 50, 100].map((quickAmount) => (
                  <button
                    key={quickAmount}
                    type="button"
                    onClick={() => setAmount(quickAmount.toString())}
                    disabled={quickAmount > parseAccountBalance(user?.accountBalance)}
                    className="py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ${quickAmount}
                  </button>
                ))}
              </div>

              {/* New Balance Preview */}
              {amount && parseFloat(amount) > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">New Balance</span>
                    <span className="font-bold text-slate-900">
                      {formatCurrency(parseAccountBalance(user?.accountBalance) - parseFloat(amount))}
                    </span>
                  </div>
                </div>
              )}

              {/* PIN */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Enter Your PIN
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                  <input
                    type="password"
                    placeholder="6-digit PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="w-full pl-11 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-center text-lg tracking-widest"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setStep('search');
                    setSearchUsername('');
                    setAmount('');
                    setPin('');
                    setRecipientFound(null);
                    navigate('/send');
                  }}
                  className="py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-all"
                >
                  Change User
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Send Money
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Info Card */}
        <div className="mt-6 bg-red-50 border border-red-100 rounded-xl p-4">
          <p className="text-sm text-red-900">
            <strong>Important:</strong> Transfers are instant and cannot be reversed. Please verify the recipient username and amount before confirming.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SendMoney;
